'use client';

import { type FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ErrorServe } from '@/components/form/groups/ErrorGroup';
import { camelToNormalize } from '@/helpers/common';
import { Modals } from '@/helpers/modals';
import { showTooltip } from '@/helpers/tooltip';
import { useNavigation } from '@/hooks/navigation';
import { useNDBFlow } from '@/hooks/useNDBFlow';
import { useGetCommonStore, useModalsStore } from '@/store';
import { REASON_FOR_UPDATING_USER } from '@/store/commonStore/slices/auth/helpers';
import { TooltipTypes } from '@/store/tooltipStore';

export const ConfirmEmail: FC<{ errorConfirmationEmail?: string | Error }> = ({
  errorConfirmationEmail,
}) => {
  const { navigate } = useNavigation();
  const useCommonStore = useGetCommonStore();

  const { t } = useTranslation('common');
  const fetchUser = useCommonStore.use.fetchUser();
  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const reasonForUpdatingUser = useCommonStore.use.reasonForUpdatingUser();
  const openModal = useModalsStore.use.open();
  const { isNDBFlow, localNDBFlow } = useNDBFlow();

  const errors = useMemo(() => {
    if (!errorConfirmationEmail) return [];

    const keys = Object.keys(errorConfirmationEmail);

    return keys.map((key) => ({
      field: key,
      message: (errorConfirmationEmail as any)[key],
    }));
  }, [errorConfirmationEmail]);

  useEffect(() => {
    if (reasonForUpdatingUser == null) return;

    if (!isAuthenticated) {
      useCommonStore.setState({ afterLoginRedirectUrl: '/' });
      openModal(Modals.Login);
      return;
    }

    if (!errorConfirmationEmail) {
      fetchUser(REASON_FOR_UPDATING_USER.EMAIL_CONFIRMATION);

      showTooltip({
        type: TooltipTypes.SUCCESS,
        body: t('verifyEmailPopup.emailConfirmed'),
      });

      if (isNDBFlow && !localNDBFlow?.isNDBEmailVerifiedShown) {
        navigate('/?modal=ndb-email-verified', true);
        return;
      }
    }

    navigate('/', true);
  }, [reasonForUpdatingUser, isAuthenticated]);

  useEffect(() => {
    if (!errors.length) return;

    showTooltip({
      type: TooltipTypes.ERROR,
      body: <ErrorTooltip errors={errors} />,
    });
  }, [errors]);

  return null;
};

export const ErrorTooltip: FC<{ errors: ErrorServe[] }> = ({ errors }) => (
  <>
    {errors.map(({ field, message }, index) => (
      <span key={index} style={{ textTransform: 'capitalize' }}>
        <span>{camelToNormalize(field)}</span>:{' '}
        {typeof message === 'string' && message}
        {Array.isArray(message) && message.join(', ')}
      </span>
    ))}
  </>
);
